import React, {useEffect, useState} from 'react'
import {Box, Grid, Skeleton, Typography} from '@mui/material';
import {getFailedBookingTransactionFees, requestGetFailedTransactionsForBooking} from '../payment-requests';
import Busy from '../../Busy';
import {withSnackbar} from "../../hocs/withSnackbar";
import TransactionItem from "./TransactionItem";
import {BookDetailsRow, PaymentHeading, PaymentTitle} from '../style'
import {getErrorMessageForNonStandardAndStandardResponse} from '../../../util/NetworkErrorUtil';

const TransactionsList = ({ bookingId, paymentMethodId, setPaymentsTotal, snackbarShowMessage }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uncollectibleCount, setUncollectibleCount] = useState(0);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (bookingId) {
        Busy.set(true);
        setLoading(true);
        try {
          const {body: transactionSummary} = await requestGetFailedTransactionsForBooking(bookingId);
          setUncollectibleCount(transactionSummary.uncollectibleCount || 0);
          let sortedTransactions = transactionSummary.bookingTransactions.sort((a, b) => a.transactionNumber - b.transactionNumber);
          // Use transactionGrossAmount (raw service charge) for initial totals since we
          // can't calculate payment processor fees without a selected payment method
          const grossTotal = sortedTransactions.reduce((acc, t) => acc + (t.transactionGrossAmount || 0), 0);
          let total = {
            count: transactionSummary.failedTransactionCount,
            charge: grossTotal,
            tax: 0,
            transactionFee: 0,
            total: grossTotal
          };
          if (paymentMethodId) {
            const transactionIds = sortedTransactions.map(transaction => transaction.id);
            const {body: failedTransactionFees} = await getFailedBookingTransactionFees(bookingId, paymentMethodId, transactionIds);
            // Build a lookup map for O(1) access when merging recomputed amounts
            const feeMap = new Map(failedTransactionFees.map(fee => [fee.transactionId, fee]));
            sortedTransactions = sortedTransactions.map(transaction => {
              const feeData = feeMap.get(transaction.id);
              return {
                ...transaction,
                recomputedBuyerTransactionAmount: feeData?.recomputedBuyerTransactionAmount
              };
            });
            const totalTransactionAmount = failedTransactionFees.reduce((acc, transaction) => acc + transaction.transactionGrossAmount, 0);
            const totalTax = failedTransactionFees.reduce((acc, transaction) => acc + transaction.transactionTaxAmount, 0);
            const totalTransactionFee = failedTransactionFees.reduce((acc, transaction) => acc + transaction.paymentProcessorFeeAmount, 0);
            const totalCharge = failedTransactionFees.reduce((acc, transaction) => acc + transaction.totalCharge, 0);
            total = {
              count: transactionSummary.failedTransactionCount,
              charge: totalTransactionAmount,
              tax: totalTax,
              transactionFee: totalTransactionFee,
              total: totalCharge
            };
          }
          setTransactions(sortedTransactions);
          setPaymentsTotal(total);
        } catch (error) {
          snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error));
        } finally {
          Busy.set(false);
          setLoading(false);
        }
      }
    }
    fetchTransactions();
  }, [bookingId, paymentMethodId, setPaymentsTotal, snackbarShowMessage]);

  return (
    <Box>
      <Grid item container direction={'column'} justifyContent='space-between' alignItems='center'>
        <PaymentHeading>
          Transaction Details
        </PaymentHeading>
        {
          uncollectibleCount > 0 &&
            <Typography sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
              {uncollectibleCount} invoice{uncollectibleCount > 1 ? 's have' : ' has'} been written off as uncollectible and {uncollectibleCount > 1 ? 'are' : 'is'} not shown below.
            </Typography>
        }
        {
          loading ?
            <Skeleton variant={'text'} width={'100%'}/>
          : !transactions.length ?
            <Typography>No transactions found.</Typography>
          :
            <Box width={'100%'}>
              <BookDetailsRow container justifyContent='space-between' alignItems='flex-end'>
                <Grid container justifyContent='space-between' alignItems='center'>
                  <PaymentTitle>Invoice</PaymentTitle>
                  <PaymentTitle>Service Dates</PaymentTitle>
                  <PaymentTitle>Service Total Charge</PaymentTitle>
                </Grid>
              </BookDetailsRow>
              {
                transactions.map((transaction) => <TransactionItem key={transaction.id} transaction={transaction} />)
              }
            </Box>
        }
      </Grid>
    </Box>
  )
}

export default withSnackbar(TransactionsList)
